import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  feedersById,
  substationsById,
  substations,
  feedersBySubstationId,
} from '../data/feederMap';
import './FeederCombobox.css';

const matchesQuery = (value, query) =>
  String(value ?? '').toLowerCase().includes(query);

const buildFilteredGroups = (query) => {
  if (!query) {
    return substations
      .map((substation) => ({
        substation,
        feeders: feedersBySubstationId[substation.id] || [],
      }))
      .filter((group) => group.feeders.length > 0);
  }

  const lowered = query.trim().toLowerCase();
  if (!lowered) {
    return buildFilteredGroups('');
  }

  const groups = [];
  substations.forEach((substation) => {
    const allFeeders = feedersBySubstationId[substation.id] || [];
    const substationMatches =
      matchesQuery(substation.name, lowered) || matchesQuery(substation.id, lowered);
    const matchedFeeders = substationMatches
      ? allFeeders
      : allFeeders.filter(
          (feeder) => matchesQuery(feeder.id, lowered) || matchesQuery(feeder.name, lowered),
        );

    if (matchedFeeders.length > 0) {
      groups.push({ substation, feeders: matchedFeeders });
    }
  });
  return groups;
};

const flattenGroups = (groups) => {
  const items = [];
  groups.forEach((group) => {
    group.feeders.forEach((feeder) => {
      items.push({ feeder, substation: group.substation });
    });
  });
  return items;
};

function FeederCombobox({ value, onChange, id, name, ariaInvalid }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listboxRef = useRef(null);
  const generatedId = useId();
  const controlId = id || `feeder-combobox-${generatedId}`;
  const listboxId = `${controlId}-listbox`;

  const groups = useMemo(() => buildFilteredGroups(query), [query]);
  const flatItems = useMemo(() => flattenGroups(groups), [groups]);

  const selectedFeeder = value ? feedersById[value] : null;
  const selectedSubstation = selectedFeeder
    ? substationsById[selectedFeeder.substationId]
    : null;
  const triggerLabel = selectedFeeder
    ? `${selectedFeeder.id}${selectedSubstation ? ` - ${selectedSubstation.name}` : ''}`
    : 'Select a feeder';

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const latestStateRef = useRef({ value, flatItems });
  latestStateRef.current = { value, flatItems };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setQuery('');
    const { value: currentValue, flatItems: currentItems } = latestStateRef.current;
    const activeIndex = currentItems.findIndex((item) => item.feeder.id === currentValue);
    setHighlightIndex(activeIndex >= 0 ? activeIndex : currentItems.length > 0 ? 0 : -1);
    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus( { preventScroll: true } );
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (highlightIndex < 0 || !listboxRef.current) {
      return;
    }
    const element = listboxRef.current.querySelector(
      `[data-option-index="${highlightIndex}"]`,
    );
    if (element) {
      element.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, isOpen]);

  const closePanel = () => {
    setIsOpen(false);
  };

  const selectFeeder = (feederId) => {
    if (feederId !== value) {
      onChange(feederId);
    }
    closePanel();
  };

  const handleTriggerClick = () => {
    setIsOpen((open) => !open);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  const handleSearchChange = (event) => {
    setQuery(event.target.value);
    setHighlightIndex(0);
  };

  const handlePanelKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (flatItems.length === 0) {
        return;
      }
      setHighlightIndex((index) => (index + 1) % flatItems.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (flatItems.length === 0) {
        return;
      }
      setHighlightIndex((index) => (index <= 0 ? flatItems.length - 1 : index - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[highlightIndex];
      if (item) {
        selectFeeder(item.feeder.id);
      }
    }
  };

  let runningIndex = -1;

  return (
    <div className="feeder-combobox" ref={containerRef}>
      <button
        type="button"
        id={controlId}
        name={name}
        role="combobox"
        className={`feeder-combobox-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-invalid={ariaInvalid || undefined}
      >
        <span className="feeder-combobox-trigger-label">
          {selectedFeeder ? (
            <>
              <strong>{selectedFeeder.id}</strong>
              {selectedSubstation && (
                <small>{selectedSubstation.name}</small>
              )}
            </>
          ) : (
            <span className="feeder-combobox-placeholder">{triggerLabel}</span>
          )}
        </span>
        <span className="feeder-combobox-chevron" aria-hidden="true">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div className="feeder-combobox-panel" onKeyDown={handlePanelKeyDown}>
          <div className="feeder-combobox-search">
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={handleSearchChange}
              placeholder="Search feeder or substation..."
              aria-label="Search feeders and substations"
              autoComplete="off"
            />
          </div>

          <div
            id={listboxId}
            ref={listboxRef}
            role="listbox"
            aria-label="Feeders grouped by substation"
            className="feeder-combobox-list"
          >
            {groups.length === 0 ? (
              <p className="feeder-combobox-empty">No matching feeders.</p>
            ) : (
              groups.map((group) => (
                <div key={group.substation.id} className="feeder-combobox-group">
                  <div className="feeder-combobox-group-header">
                    {group.substation.name}
                  </div>
                  <ul>
                    {group.feeders.map((feeder) => {
                      runningIndex += 1;
                      const optionIndex = runningIndex;
                      const isSelected = feeder.id === value;
                      const isHighlighted = optionIndex === highlightIndex;
                      return (
                        <li
                          key={feeder.id}
                          role="option"
                          aria-selected={isSelected}
                          data-option-index={optionIndex}
                          className={`feeder-combobox-option ${
                            isSelected ? 'is-selected' : ''
                          } ${isHighlighted ? 'is-highlighted' : ''}`}
                          onMouseEnter={() => setHighlightIndex(optionIndex)}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            selectFeeder(feeder.id);
                          }}
                        >
                          <span>{feeder.id}</span>
                          {isSelected && (
                            <span className="feeder-combobox-check" aria-hidden="true">
                              ✓
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FeederCombobox;
